import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Smartphone, Bell, Link2Off, Home, ChevronDown, Clock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import HouseholdsModal from "./HouseholdsModal";
import ConfirmModal from "./ConfirmModal";

const ROLES = ["gebruiker", "beheerder", "super_admin"];
const PLATFORM = { ios: "iOS", android: "Android", windows: "Windows", macos: "macOS", chromeos: "ChromeOS", linux: "Linux", web: "Web" };
const fmtDate = (d) => d ? new Date(d).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const DAY = 86400000;
const daysSince = (d) => d ? Math.floor((Date.now() - new Date(d).getTime()) / DAY) : null;
const relActive = (d) => {
  const n = daysSince(d);
  if (n == null) return "nooit";
  if (n <= 0) return "vandaag";
  if (n === 1) return "gisteren";
  if (n < 30) return `${n} dagen geleden`;
  if (n < 365) return `${Math.floor(n / 30)} mnd geleden`;
  return `${Math.floor(n / 365)} jaar geleden`;
};
const INACTIVE_DAYS = 14;
const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "active", label: `Actief (≤${INACTIVE_DAYS}d)` },
  { id: "inactive", label: `Inactief (>${INACTIVE_DAYS}d)` },
  { id: "never", label: "Nooit actief" },
];

export default function Users({ meId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");
  const [hhModal, setHhModal] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [devTarget, setDevTarget] = useState(null);

  const load = () => api.get("/admin/users-overview").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    setBusy(uid);
    try { await api.put(`/users/${uid}`, { role }); toast.success("Rol bijgewerkt"); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const del = async () => {
    const u = delTarget; if (!u) return;
    setBusy(u.user_id); setDelTarget(null);
    try { await api.delete(`/users/${u.user_id}`); toast.success("Gebruiker verwijderd"); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const unlinkDevice = async () => {
    const dt = devTarget; if (!dt) return;
    setDevTarget(null);
    try { await api.delete(`/admin/devices/${dt.deviceId}`); toast.success("Apparaat ontkoppeld"); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!rows) return <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>;

  const matchFilter = (u) => {
    const n = daysSince(u.last_active);
    if (filter === "active") return n != null && n <= INACTIVE_DAYS;
    if (filter === "inactive") return n != null && n > INACTIVE_DAYS;
    if (filter === "never") return n == null;
    return true;
  };
  const counts = {
    all: rows.length,
    active: rows.filter((u) => { const n = daysSince(u.last_active); return n != null && n <= INACTIVE_DAYS; }).length,
    inactive: rows.filter((u) => { const n = daysSince(u.last_active); return n != null && n > INACTIVE_DAYS; }).length,
    never: rows.filter((u) => daysSince(u.last_active) == null).length,
  };
  const shown = rows.filter(matchFilter);

  return (
    <div className="space-y-3" data-testid="sa-users-wrap">
      <div className="flex flex-wrap gap-1.5" data-testid="sa-users-filters">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`sa-users-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${filter === f.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {f.label} <span className={filter === f.id ? "opacity-80" : "text-slate-400"}>({counts[f.id]})</span>
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-2 overflow-x-auto" data-testid="sa-users">
        <table className="w-full text-sm min-w-[940px]">
          <thead>
            <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-200">
              <th className="py-3 px-2">Naam</th><th className="px-2">E-mail</th>
              <th className="px-2">Huishoudens</th><th className="px-2">Apparaten</th>
              <th className="px-2">Laatst actief</th>
              <th className="px-2">Rol</th><th className="px-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => (
            <Fragment key={u.user_id}>
              <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sa-user-row-${u.user_id}`}>
                <td className="py-3 px-2 font-bold">{u.name}{u.has_pin && <span className="ml-1.5 text-[10px] font-bold text-emerald-600" title="Heeft PIN">PIN</span>}</td>
                <td className="px-2 text-slate-500">{u.email || "—"}</td>
                <td className="px-2">
                  {u.household_count === 0
                    ? <span className="text-slate-300">—</span>
                    : (
                      <button data-testid={`sa-user-households-${u.user_id}`} onClick={() => setHhModal(u)}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold hover:brightness-95 ${u.household_count > 1 ? "text-amber-700 bg-amber-50 border border-amber-200" : "text-slate-600 bg-slate-100 border border-slate-200"}`}>
                        <Home className="w-3 h-3" />
                        {u.household_count === 1 ? u.households[0].name : `${u.household_count} huishoudens`}
                      </button>
                    )}
                </td>
                <td className="px-2">
                  <button data-testid={`sa-user-devices-toggle-${u.user_id}`} onClick={() => setOpen(open === u.user_id ? null : u.user_id)}
                    disabled={u.device_count === 0 && u.push_count === 0}
                    className="inline-flex items-center gap-1.5 disabled:opacity-40">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${u.installed_count > 0 ? "text-emerald-700 bg-emerald-50 border border-emerald-200" : u.device_count > 0 ? "text-slate-600 bg-slate-100" : "text-slate-400"}`}>
                      <Smartphone className="w-3 h-3" />{u.installed_count > 0 ? `${u.installed_count} geïnst.` : u.device_count > 0 ? `${u.device_count} app.` : "geen"}
                    </span>
                    {u.push_count > 0 && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200"><Bell className="w-3 h-3" />{u.push_count}</span>}
                    {u.device_count > 0 && <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open === u.user_id ? "rotate-180" : ""}`} />}
                  </button>
                </td>
                <td className="px-2">
                  {(() => {
                    const n = daysSince(u.last_active);
                    const cls = n == null ? "text-slate-300" : n > INACTIVE_DAYS ? "text-rose-600" : "text-slate-500";
                    return <span data-testid={`sa-user-lastactive-${u.user_id}`} title={fmtDate(u.last_active)} className={`inline-flex items-center gap-1 text-xs font-semibold ${cls}`}><Clock className="w-3 h-3" />{relActive(u.last_active)}</span>;
                  })()}
                </td>
                <td className="px-2">
                  <select value={u.role} disabled={busy === u.user_id || u.user_id === meId}
                    onChange={(e) => changeRole(u.user_id, e.target.value)} data-testid={`sa-user-role-${u.user_id}`}
                    className="bg-white border border-slate-300 rounded-md px-2 py-1 text-xs outline-none disabled:opacity-50">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-2 text-right">
                  <button onClick={() => setDelTarget(u)} disabled={busy === u.user_id || u.user_id === meId}
                    data-testid={`sa-user-delete-${u.user_id}`}
                    className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-md px-2 py-1 text-xs font-bold disabled:opacity-30">
                    <Trash2 className="w-3.5 h-3.5" /> Verwijderen
                  </button>
                </td>
              </tr>
              {open === u.user_id && u.devices.length > 0 && (
                <tr className="bg-slate-50/60" data-testid={`sa-user-devices-${u.user_id}`}>
                  <td colSpan={7} className="px-3 py-2">
                    <div className="space-y-1.5">
                      {u.devices.map((d) => (
                        <div key={d.device_id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs max-w-2xl">
                          <Smartphone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-slate-700">{PLATFORM[d.platform] || d.platform || "Onbekend"}{d.installed ? " · geïnstalleerd" : ""}</p>
                            <p className="text-slate-400 truncate">Laatst actief {fmtDate(d.last_seen)}</p>
                          </div>
                          <button data-testid={`sa-device-unlink-${d.device_id}`} onClick={() => setDevTarget({ deviceId: d.device_id, name: u.name })}
                            className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-md px-2 py-1 font-bold shrink-0">
                            <Link2Off className="w-3 h-3" /> Ontkoppel
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>

      <HouseholdsModal user={hhModal} onClose={() => setHhModal(null)} />

      <ConfirmModal open={!!devTarget} title="Apparaat ontkoppelen?"
        message={devTarget ? `Het apparaat van ${devTarget.name} wordt losgekoppeld en moet opnieuw inloggen.` : ""}
        confirmLabel="Ontkoppelen" onConfirm={unlinkDevice} onCancel={() => setDevTarget(null)} />

      {delTarget && (
        <div className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDelTarget(null)} data-testid="sa-user-delete-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-3"><Trash2 className="w-6 h-6 text-rose-600" /></div>
            <p className="font-head font-black text-lg">Gebruiker verwijderen?</p>
            <p className="text-sm text-slate-500 mt-1">Weet je zeker dat je <span className="font-bold text-slate-700">{delTarget.name}</span> definitief wilt verwijderen? Dit kan niet ongedaan worden gemaakt.</p>
            <div className="flex gap-2 mt-5">
              <button data-testid="sa-user-delete-cancel" onClick={() => setDelTarget(null)} className="flex-1 rounded-xl border border-slate-200 py-2.5 font-bold text-sm hover:bg-slate-50">Annuleren</button>
              <button data-testid="sa-user-delete-confirm" onClick={del} className="flex-1 rounded-xl bg-rose-600 text-white py-2.5 font-bold text-sm hover:bg-rose-700">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
